// SceneryPlacer — Set13 §3.3/§3.7 → Set17: stable scenery placement.
//
// WHY JITTERED GRID over Poisson-disk / dart-throwing (Set13 §3.3):
//   1. It is the codebase's proven idiom (the material `scatter` presence gate that fixed the
//      lattice-row artifacts, including the negative-coordinate cast rule).
//   2. O(1) per instance, no neighbourhood, border handling free. An instance belongs to exactly
//      one cell, and the cell is a pure function of world coordinates.
//   3. Tile size stops being a placement input — placement is defined per CELL.
//
// SET17 — THREE-ROLES DECOMPOSITION (the design lesson that makes edit-stability possible):
//   A candidate lattice played three distinct roles, and ONLY one needs a lattice:
//   1. CANDIDATE GENERATOR — where candidate positions come from. (per-region lattice — Set17)
//   2. RNG DOMAIN — cell coords feed presence/jitter/variant/rank hashes → determinism +
//      tile-invariance. (per-region-seeded cell coords — Set17)
//   3. COLLISION SEMANTICS — "one instance per cell" prevented dual-placement. (Set17 REPLACED by
//      an explicit spatial-hash arbiter with REAL trunk/crown radii, decoupled from the lattice.)
//
// The OLD bug: ONE lattice was shared across every region overlapping a tile, its spacing a `min`
// over all their spacing knobs. Because the lattice's cell coords fed every RNG stream, ANY
// densifying edit (adding a feature, tightening a spacing, a sibling kind's spacing) re-rolled the
// positions/existence of OTHER features. Set17 makes each region's candidates a PURE function of
// that region's own data + identity (per-region lattice + region-id-seeded streams), and moves
// cross-feature exclusion to the arbiter. Stability contract:
//
//   > An edit to feature A changes only (a) instances A itself places, (b) cells A's geometry
//   > newly covers/frees — locally, at A's footprint — and (c) intended suppression effects.
//   > Nothing else re-rolls.
//
// SCALE SAFETY (§3.7 — the no-crash invariant): peak memory + per-frame instance count are
// functions of CAMERA STATE + a FIXED BUDGET, never of world/region area or zoom. Placement
// produces a CANONICAL geometric layout (zoom- & budget-independent — the arbiter sees the full
// candidate set); rank-thinning + the budget are EMISSION-time filters over a stable, nested
// subset of it (zoom out only removes, never reshuffles — no pop).
//
// ⚠ NEGATIVE-COORDINATE RULE (§8): the cell hash must mirror GLSL `uvec2(ivec2(cell))`, i.e. wrap
// the signed lattice coordinate to uint32 via `>>> 0` BEFORE hashing. A negative float→uint is
// undefined in GLSL (drivers clamp to 0 → the cell hash collapses to a function of y alone). This
// is invisible at positive coordinates and must be tested explicitly.
import {SceneryCollision, SceneryKindDef, SceneryVariant} from "../@SceneryTypes.js";
import {SceneryKindEntry} from "../@SceneryFeature.js";
import {SceneryBudgetConfig, resolveBudgetNumber} from "../@SceneryBudgetConfig.js";
import {hash01, hash01_3, hashString32, resolveCollision, resolveNumber, scenerySeedOffset} from "../SceneryHash.js";
import {timeIt} from "../Render/SceneryTiming.js";

// ⚠ TEMP DEBUG FLAG (2026-08-15) — "spacing change → 0 instances" diagnosis. Set false when done.
const SCENERY_PLACER_DEBUG = false;

/** The spatial-hash bucket size (metres) — Set17. PURELY a performance knob: the OUTPUT is
 *  byte-identical for any bucket size (the brute-force cross-check test pins this). Sized ~the
 *  typical tree footprint diameter so most collision queries touch one bucket. */
export const SCENERY_COLLISION_BUCKET = 8;

/** One placed instance, ready to render (or be culled). The variant index references the baked
 *  atlas cell; rotation/scale/tint are the FREE (instance-level) jitter applied at draw time. */
export interface SceneryInstance {
	x: number;
	y: number;
	scale: number;
	rotation: number;
	tint: number;
	variantIndex: number;
	depthKey: number;
	/** Stable per-instance hash for RANK THINNING (independent of the presence hash — §3.7 Dial 1). */
	rank: number;
}

/** The subset of a region the placer reads. Structural, so BOTH a real `SceneryRegion` (from the
 *  bridge) and an inline test fixture satisfy it. Set17: the region is now the placement UNIT —
 *  its own lattice + its own RNG seed. */
export type PlacementRegionLike = {
	/** Stable region id (path_id) — drives BOTH the per-region lattice seed (Set17) and the Set16
	 *  kind-draw CIP seed (§3), so overlapping regions / colliding paths don't create correlated
	 *  patterns, and so an edit to another feature can't re-roll this region's streams. */
	id: string;
	bbox: {x: number; y: number; width: number; height: number};
	geoFactorAt: (x: number, y: number) => number;
	/** Per-region presence override (0..1) — the region's SHARED occupancy presence. Absent = max
	 *  sibling presence (or the kind's own, for a single-kind region). */
	presenceOverride?: number|n;
	/** Per-region spacing override (metres) — this region's OWN lattice pitch. Absent = the finest
	 *  spacing among the region's own kinds. PURELY a density knob (Set17 §2) — decoupled from
	 *  collision. */
	spacingOverride?: number|n;
	/** RESOLVED placement seed (Set17 §2): `pathFeature.seedOverride` ?? 0 (the bridge resolves the
	 *  optional feature field to a plain number). A NON-ZERO value EXCLUSIVELY perturbs THIS
	 *  region's RNG streams — presence gate, position jitter, and the mixed-kind draw (everything
	 *  that keys off `regionSeed`) — WITHOUT changing the region's geometry/bbox, density
	 *  (occupancy/spacing/presence/feather), kinds/weights, dominance, suppression, or the
	 *  per-instance RANK stream (rank is deliberately excluded so zoom-out thinning is unaffected).
	 *  Absent/0 keeps the pure id-based seed (unchanged behaviour). */
	seed?: number;
	/** RESOLVED kind-seed (`kindSeedOverride`, OPTIONAL — absent vs 0 are distinct). WHEN SET it
	 *  REPLACES `seed` for the MIXED-KIND DRAW ONLY, so a good distribution (held by `seed`) is kept
	 *  while re-rolling which kind owns each cell. Absent → kind draw follows `seed`. */
	kindSeed?: number|n;
	/** The region's NORMALISED kind list (ids + weights; the placer draws cell ownership from it).
	 *  EMPTY/absent (a suppression-only region): NO density, owns no cells — can only suppress. */
	kinds?: SceneryKindEntry[];
	/** RESOLVED dominance (explicit ?? derived nesting). Higher-dominance candidates are inserted
	 *  into the arbiter FIRST (they win contested spots over lower-dominance candidates). Absent = 0. */
	dominance?: number;
	/** RESOLVED suppression fraction [0,1]. Each strictly-higher-dominance region with this set
	 *  multiplies this region's density by `(1 − suppress × geoFactor)` (feathered at its boundary).
	 *  Set17 D4: SUPPRESSION IS UNCHANGED — it stays a generation-side density gate (it only ever
	 *  removes, never moves), NOT an arbiter operation. */
	suppressPriorScenery?: number;
};

/** The subset of kind fields the placer reads — satisfied by BOTH `SceneryKindDef` and
 *  `ResolvedSceneryKind`. `collision` (Set17) carries the trunk/crown radii. */
export type PlacementKindLike = Pick<SceneryKindDef, "id" | "spacing" | "presence" | "jitter" | "seed"> & {
	collision?: SceneryCollision|n;
};

export interface PlaceSceneryTileOpts {
	/** The regions overlapping this tile (∪ margin band). */
	regions: PlacementRegionLike[];
	/** Resolve a kind's placement params by id (spacing/presence/seed/jitter/collision). Absent →
	 *  that kind contributes no candidates (defensive; the bridge already dropped unresolvable ids).
	 *  This replaces the old single `kind` + `resolveSiblingKind` pair — placement is now per-region
	 *  and single-pass over all regions (§4). */
	resolveKind: (kindID: string) => PlacementKindLike | null;
	/** Resolve a kind's derived variants by id — for the per-cell variant draw + depthKey. */
	resolveVariants: (kindID: string) => SceneryVariant[] | null;
	/** Global budget (caps + mpp fade). */
	budget: SceneryBudgetConfig;
	/** Tile bounds in world metres (minX, minY, size). Placement is per-CELL, so the instance SET is
	 *  invariant to tile size — only buffer packing depends on it. The arbiter runs over the tile ∪
	 *  the D5 margin band (candidates in the band arbitrate edge collisions but are NOT emitted). */
	minX: number;
	minY: number;
	tileSize: number;
	/** Metres-per-pixel at the current zoom — drives the rank-thinning threshold (§3.7 Dial 1).
	 *  Supply 0 for tests that want full density. */
	mpp: number;
	/** Optional per-instance presence multiply from a world-space fbm field (glades/stands/edge
	 *  thinning) + masks — the placer multiplies it into the region's density. Absent = 1. */
	densityFieldAt?: (x: number, y: number) => number;
	/** When true, the returned `placedByRegion` map is populated (region id → number of emitted
	 *  instances originating from that region). Only the UI's "Placed: X" readout needs it; the
	 *  renderer leaves it off to skip the bookkeeping. */
	collectRegionCounts?: boolean;
}

/** The per-tile placement result. Instances are grouped by KIND (the renderer's per-kind passes
 *  read from it); when `opts.collectRegionCounts` is set, `placedByRegion` additionally reports
 *  how many emitted instances each REGION contributed (the UI's density readout). */
export interface SceneryTileResult {
	/** kindID → placed instances for this tile. */
	instancesByKind: Map<string, SceneryInstance[]>;
	/** region id → number of emitted instances originating from that region. Only populated when
	 *  `opts.collectRegionCounts` is set (else empty). */
	placedByRegion: Map<string, number>;
}

/** Resolved per-(region, kind) placement params — what the placer actually reads (kind
 *  independently resolved so a single `resolveKind` map needn't be re-walked per cell). */
interface ResolvedRegionKind {
	kindID: string;
	weight: number;
	kind: PlacementKindLike;
	spacing: number;
	presence: number;
	collision: SceneryCollision;
	variants: SceneryVariant[];
}

/** Per-region placement metadata (the region's own lattice + seed + occupancy + resolved kinds). */
interface RegionMeta {
	region: PlacementRegionLike;
	/** This region's OWN candidate lattice pitch (spacingOverride ?? finest own-kind spacing). */
	lattice: number;
	/** Region-id-seeded RNG base (decorrelates this region's streams from every other's). */
	regionSeed: number;
	/** Shared occupancy presence (presenceOverride ?? max sibling presence ?? kind default). */
	occupancy: number;
	/** Resolved kinds that yielded a resolveKind (empty = places nothing, can only suppress). */
	kinds: ResolvedRegionKind[];
	dominance: number;
}

/** One candidate before arbitration (generation-side). The sort + arbiter decide its fate. */
interface Candidate {
	region: RegionMeta;
	kindID: string;
	x: number;
	y: number;
	trunkR: number;
	crownR: number;
	/** Lower = processed first (arbiter winner at equal dominance). Stable, parameter-free (§8). */
	tiebreak: number;
	rank: number;
	variantIndex: number;
	scale: number;
	rotation: number;
	tint: number;
	depthKey: number;
}

/** An entry in the collision hash (the arbiter's placed set). ONLY geometry + the source region —
 *  dominance/tiebreak are handled by insertion order (pre-sorted), so an overlapping already-inserted
 *  entry is by construction a "winner" and the candidate simply loses. */
interface ArbiterEntry {
	x: number;
	y: number;
	trunkR: number;
	crownR: number;
	/** The source region id. Intra-region pairs are SKIPPED by the arbiter (a region's OWN lattice
	 *  spacing is the within-region placement authority — Set16's "one object per own-lattice cell"
	 *  invariant; the arbiter only coordinates CROSS-region placement). Without this, jittered
	 *  adjacent same-region cells crown-overlap and the arbiter deletes one → gaps, and the
	 *  per-tile arbiter state differs between tile builds → tile-partition non-invariance. */
	regionID: string;
}

/** The per-tile spatial-hash arbiter (Set17 §3.2). Bucket size is perf-only; the output is
 *  invariant to it (a brute-force cross-check test pins this). */
class CollisionHash {
	private buckets = new Map<number, ArbiterEntry[]>();
	constructor(private bucketSize: number) {}

	private key(bx: number, by: number): number {
		return (Math.imul(bx, 0x9E3779B1) ^ Math.imul(by, 0x85EBCA77)) >>> 0;
	}

	insert(e: ArbiterEntry): void {
		const bx = Math.floor(e.x / this.bucketSize);
		const by = Math.floor(e.y / this.bucketSize);
		const key = this.key(bx, by);
		let list = this.buckets.get(key);
		if (list == null) { list = []; this.buckets.set(key, list); }
		list.push(e);
	}

	/** Broad-phase: return every entry whose bucket could hold an entry within `r` of (x, y). The
	 *  caller does the exact distance check. Visits the rectangle of buckets covered by the query
	 *  disc (the bucket-edge gather — the one real correctness hazard, pinned by the cross-check).
	 *  PERF (2026-08-28): the arbitrate hot loop only needs to know whether ANY entry matches
	 *  `predicate` — use {@link queryAny} (non-allocating, short-circuits) instead of building a
	 *  fresh `out` array per candidate (~10 k small array allocations per tile). */
	query(x: number, y: number, r: number): ArbiterEntry[] {
		const out: ArbiterEntry[] = [];
		const bx0 = Math.floor((x - r) / this.bucketSize);
		const bx1 = Math.floor((x + r) / this.bucketSize);
		const by0 = Math.floor((y - r) / this.bucketSize);
		const by1 = Math.floor((y + r) / this.bucketSize);
		for (let bx = bx0; bx <= bx1; bx++) {
			for (let by = by0; by <= by1; by++) {
				const list = this.buckets.get(this.key(bx, by));
				if (list != null) for (const e of list) out.push(e);
			}
		}
		return out;
	}

	/** PERF (2026-08-28): non-allocating broad-phase test — true iff any entry in the buckets the
	 *  disc covers satisfies `predicate`. Short-circuits on the first match (the arbitrate loop's
	 *  "is there a conflict?" question), and never builds a result array. Bucket set identical to
	 *  {@link query}; the caller's `predicate` is the exact distance check. */
	queryAny(x: number, y: number, r: number, predicate: (e: ArbiterEntry)=>boolean): boolean {
		const bx0 = Math.floor((x - r) / this.bucketSize);
		const bx1 = Math.floor((x + r) / this.bucketSize);
		const by0 = Math.floor((y - r) / this.bucketSize);
		const by1 = Math.floor((y + r) / this.bucketSize);
		for (let bx = bx0; bx <= bx1; bx++) {
			for (let by = by0; by <= by1; by++) {
				const list = this.buckets.get(this.key(bx, by));
				if (list == null) continue;
				for (let i = 0; i < list.length; i++) if (predicate(list[i])) return true;
			}
		}
		return false;
	}
}

/** The Set17 conflict predicate (D2): rule 1 (trunk) is ALWAYS enforced between any two KINDS from
 *  DIFFERENT regions; rule 2 (crown) only when BOTH parties have a non-null crown (a flower — crown
 *  null — never crown-collides with a pine's crown, but DOES trunk-collide with its trunk). The
 *  dominance / equal-dominance tiebreak is handled by INSERTION ORDER (pre-sorted), so any
 *  cross-region overlap → reject. SAME-region pairs are NEVER a conflict (see ArbiterEntry.regionID:
 *  a region's own lattice is the within-region authority). */
function overlaps(e: ArbiterEntry, c: Candidate): boolean {
	if (e.regionID == c.region.region.id) return false; // intra-region: lattice is the authority
	const dx = e.x - c.x, dy = e.y - c.y;
	const d2 = dx * dx + dy * dy;
	const trunkSum = e.trunkR + c.trunkR;
	if (d2 < trunkSum * trunkSum) return true;
	if (c.crownR > 0 && e.crownR > 0) {
		const crownSum = e.crownR + c.crownR;
		if (d2 < crownSum * crownSum) return true;
	}
	return false;
}

/** Resolve the region's SHARED OCCUPANCY presence (Set16 §2): region `presenceOverride` wins when
 *  set; else the MAX of the region's resolved kinds' presence (a sparse sibling never thins the
 *  mixed lattice — weight controls the mix). Falls back to 0.8 (the kind default) when no kind
 *  resolves. */
function occupancyPresence(region: PlacementRegionLike, kinds: ResolvedRegionKind[]): number {
	if (region.presenceOverride != null) return resolveNumber(region.presenceOverride, 1);
	let m = 0;
	for (const k of kinds) {
		const p = resolveNumber(k.presence, 0.8);
		if (p > m) m = p;
	}
	return m > 0 ? m : 0.8;
}

/** Place scenery into a tile — Set17 single-pass over ALL regions (was per-kind). Deterministic
 *  from world coordinates + each region's OWN data/identity (NOT from tile size — per-CELL).
 *  Returns a {@link SceneryTileResult} (per-kind instances, + optionally per-region counts), so the
 *  renderer's per-kind passes read from ONE placement per tile (§4) — the margin band + arbiter
 *  run once, not once per kind. */
export function placeSceneryTile(opts: PlaceSceneryTileOpts): SceneryTileResult {
	return timeIt("placeSceneryTile", ()=>placeSceneryTile_inner(opts));
}

/** The un-timed body of {@link placeSceneryTile} (wrapped above for timing). */
function placeSceneryTile_inner(opts: PlaceSceneryTileOpts): SceneryTileResult {
	const {budget} = opts;
	const maxInstancesPerTile = resolveBudgetNumber(budget.maxInstancesPerTile, 4000);
	const maxInstancesTotal = resolveBudgetNumber(budget.maxInstancesTotal, 300000);
	const mppFadeStart = resolveBudgetNumber(budget.mppFadeStart, 1.5);
	const mppFadeEnd = resolveBudgetNumber(budget.mppFadeEnd, 6.0);

	const size = resolveNumber(opts.tileSize, 128);
	const minX = resolveNumber(opts.minX, 0);
	const minY = resolveNumber(opts.minY, 0);

	// ── Resolve per-region metadata (the region's own lattice + seed + occupancy + kinds). ──
	const regionMetas: RegionMeta[] = [];
	let globalMaxTrunk = 0, globalMaxCrown = 0;
	for (const region of opts.regions) {
		const kinds = region.kinds ?? [];
		const resolvedKinds: ResolvedRegionKind[] = [];
		let minSpacing = Infinity;
		for (const k of kinds) {
			const kind = opts.resolveKind(k.kindID);
			if (kind == null) continue; // unresolvable → contributes no candidates (defensive)
			const spacing = resolveNumber(kind.spacing, 6);
			const variants = opts.resolveVariants(k.kindID) ?? [];
			if (variants.length == 0) continue; // no atlas → can't place
			const collision = resolveCollision(spacing, kind.collision);
			if (collision.trunk > globalMaxTrunk) globalMaxTrunk = collision.trunk;
			if (collision.crown != null && collision.crown > globalMaxCrown) globalMaxCrown = collision.crown;
			if (spacing < minSpacing) minSpacing = spacing;
			resolvedKinds.push({kindID: k.kindID, weight: k.weight ?? 1, kind, spacing, presence: kind.presence, collision, variants});
		}
		const lattice = region.spacingOverride != null
			? Math.max(resolveNumber(region.spacingOverride, 6), 1e-3)
			: (Number.isFinite(minSpacing) ? minSpacing : 6);
		regionMetas.push({
			region,
			lattice,
			// Region-id seed, PLUS the region's authored seed-override. Absent/0 → the pure id-based
			// seed (Set17 §2, unchanged); a non-zero `region.seed` EXCLUSIVELY perturbs THIS region's
			// streams (its own edit never re-rolls any other region's).
			regionSeed: (hashString32(`sceneryRegion:${region.id}`) + resolveNumber(region.seed, 0)) >>> 0,
			occupancy: occupancyPresence(region, resolvedKinds),
			kinds: resolvedKinds,
			dominance: resolveNumber(region.dominance, 0),
		});
	}

	// ── D5 margin: the band around the tile whose candidates arbitrate EDGE collisions but are
	// not emitted. Width = the max collision reach over the region union. ──
	const M = Math.max(globalMaxTrunk, globalMaxCrown, 1e-3);

	// ── Expected placed count (for the rank-thinning keepFrac — keeps total near maxInstancesTotal).
	let expected = 0;
	for (const rm of regionMetas) {
		expected += ((size + 2 * M) / rm.lattice) * ((size + 2 * M) / rm.lattice) * 0.5 * rm.occupancy;
	}
	const keepFrac = Math.min(1, maxInstancesTotal / Math.max(expected, 1));
	const zoomFade = 1 - smoothstep(mppFadeStart, mppFadeEnd, opts.mpp);
	const threshold = keepFrac * zoomFade;

	// ⚠ TEMP DEBUG — "spacing change → 0 instances" diagnosis.
	if (SCENERY_PLACER_DEBUG) {
		console.log(`[SceneryDebug] placer tile=[${minX},${minY},${size}] margin=${M.toFixed(2)} regions=${opts.regions.length} ` +
			`cellsPerRegion=${regionMetas.map(rm=>`${rm.region.id}:${rm.lattice.toFixed(2)}@occ${rm.occupancy.toFixed(2)}`).join(",")} threshold=${threshold.toFixed(3)}`);
	}

	// ── Generate candidates (per-region lattices, region-seeded streams). ──
	const candidates: Candidate[] = [];
	timeIt("placer.candidates", ()=>{
	for (const rm of regionMetas) {
		if (rm.kinds.length == 0) continue; // suppression-only: no density, no candidates
		const {region, lattice, regionSeed, occupancy} = rm;
		// ⚠ BROAD-PHASE (2026-08-28 perf): the region's own `geoFactorAt` was called for EVERY
		// lattice cell across the WHOLE tile+margin band, even cells far outside the region — a
		// 512 m tile with 9 sparse regions did 9× full-tile distance scans per tile, and
		// `geoFactorAt` → `distToPolyline` (a per-edge loop) dominated the load (14 s + 10 s in the
		// profiler trace). The region `bbox` is inflated by `strokeWidth/2 + feather/2` — the EXACT
		// extent of the positive `geoFactorAt` region (see `collectSceneryRegions`). So we clamp the
		// lattice iteration to (tile+margin) ∩ bbox, guaranteeing we never skip a cell whose density
		// could be > 0. This is the same broad-phase the suppression loop below already applies to
		// suppressors — the own-region gate was simply missing.
		const bandMinX = minX - M, bandMaxX = minX + size + M;
		const bandMinY = minY - M, bandMaxY = minY + size + M;
		const rb = region.bbox;
		if (bandMinX > rb.x + rb.width || bandMaxX < rb.x || bandMinY > rb.y + rb.height || bandMaxY < rb.y) continue;
		const iterMinX = Math.max(bandMinX, rb.x), iterMaxX = Math.min(bandMaxX, rb.x + rb.width);
		const iterMinY = Math.max(bandMinY, rb.y), iterMaxY = Math.min(bandMaxY, rb.y + rb.height);
		const x0 = Math.floor(iterMinX / lattice);
		const y0 = Math.floor(iterMinY / lattice);
		const x1 = Math.ceil(iterMaxX / lattice);
		const y1 = Math.ceil(iterMaxY / lattice);
		for (let cy = y0; cy < y1; cy++) {
			for (let cx = x0; cx < x1; cx++) {
				const cellX = cx * lattice, cellY = cy * lattice;
				const cxW = cellX + lattice * 0.5, cyW = cellY + lattice * 0.5;

				// Density (generation-side): occupancy × region geo × suppression × fbm/mask.
				let density = occupancy;
				const geo = region.geoFactorAt(cxW, cyW);
				if (geo <= 0) continue;
				density *= geo;
				// Suppression (D4 unchanged): every strictly-higher-dominance suppressor's feathered
				// factor compounds into THIS region's density gate.
				const rDominance = rm.dominance;
				for (const s of regionMetas) {
					const suppress = s.region.suppressPriorScenery;
					if (!(typeof suppress == "number" && suppress > 0)) continue;
					if (!(s.dominance > rDominance)) continue;
					if (cxW < s.region.bbox.x || cxW > s.region.bbox.x + s.region.bbox.width) continue;
					if (cyW < s.region.bbox.y || cyW > s.region.bbox.y + s.region.bbox.height) continue;
					const sf = s.region.geoFactorAt(cxW, cyW);
					if (sf <= 0) continue;
					density *= 1 - Math.min(suppress, 1) * sf;
				}
				if (opts.densityFieldAt != null) density *= opts.densityFieldAt(cxW, cyW);
				density = Math.max(0, Math.min(1, density));
				if (density <= 0) continue;

				// Presence gate — REGION-seeded (all kinds in the region agree on which cells spawn,
				// so the mixed draw below is consistent → one instance per own-lattice cell).
				const presenceHash = hash01(cx + regionSeed + 311, cy + regionSeed + 211);
				if (presenceHash >= density) continue;

				// Set16 §3 — the MIXED-KIND DRAW, region-local (a pure function of this region's own
				// kinds + id + kind-seed — an edit re-rolls only this region). Single-kind fast path
				// skips the roll. The base salt folds the kind-draw seed: `kindSeed` WHEN SET, else
				// `seed` — so a `kindSeedOverride` re-rolls ONLY which kind owns each cell (distribution
				// untouched); absent/0 both → pure id-based salt (byte-identical to the single-seed
				// behaviour).
				let kindID: string;
				if (rm.kinds.length > 1) {
					const kindSalt = hashString32(`sceneryKindDraw:${region.id}`) >>> 0;
					const kindSeed = resolveNumber(region.kindSeed != null ? region.kindSeed : region.seed, 0);
					const kd = (kindSalt + kindSeed) >>> 0;
					const kHash = hash01(cx + kd + 13, cy + kd + 29);
					const drawnIndex = pickWeighted(kHash, rm.kinds);
					kindID = rm.kinds[drawnIndex].kindID;
				} else {
					kindID = rm.kinds[0].kindID;
				}
				const rk = rm.kinds.find(k=>k.kindID == kindID);
				if (rk == null) continue;

				// RANK — REGION-seeded (stable per instance; the DISPLAY filter selects a nested
				// subset by it, zoom-out only removes). Independent of presence (fixed sub-offset).
				const rank = hash01(cx + regionSeed + 53, cy + regionSeed + 97);

				// Position jitter — REGION-seeded (the cell's instance position is region-owned; a
				// kind edit can't move a mixed cell's position).
				const placeJitter = 0.7;
				const [jx, jy] = hash01_3(cx + regionSeed, cy + regionSeed);
				const px = cellX + lattice * (0.5 + (jx - 0.5) * placeJitter);
				const py = cellY + lattice * (0.5 + (jy - 0.5) * placeJitter);

				// Variant — KIND-seeded (variant is kind-specific).
				const KSO = scenerySeedOffset(rk.kind.seed);
				const vHash = hash01(cx + KSO + 1009, cy + KSO + 2017);
				const variantIndex = pickWeighted(vHash, rk.variants);
				// Instance jitter — KIND-seeded (rotation/scale/tint are kind-specific appearance).
				const rotHash = hash01(cx + KSO + 3, cy + KSO + 5);
				const scHash = hash01(cx + KSO + 7, cy + KSO + 11);
				const tintHash = hash01(cx + KSO + 13, cy + KSO + 17);
				const rotation = rotHash * Math.PI * 2 * resolveNumber(rk.kind.jitter?.rotation, 1);
				const scaleMin = resolveNumber(rk.kind.jitter?.scale_min, 1);
				const scaleMax = resolveNumber(rk.kind.jitter?.scale_max, 1);
				const scale = scaleMin + (scaleMax - scaleMin) * scHash;
				const tintSwing = resolveNumber(rk.kind.jitter?.tintSwing, 0.12);
				const tint = 1 + (tintHash * 2 - 1) * tintSwing;

				// D3 — the deterministic identity tie-break: (region, kind)-salted, cell-based.
				// Parameter-free → a feature's settings never affect the tie-break outcome.
				const salt = (hashString32(region.id) + hashString32(kindID)) >>> 0;
				const tiebreak = hash01(cx + salt + 71, cy + salt + 79);

				// depthKey = targetHeight × scale (§3.6 ordering key — NOT terrain elevation).
				const thParam = rk.variants[variantIndex]?.params?.targetHeight;
				const depthKey = resolveNumber(typeof thParam == "number" ? thParam : undefined, 12) * scale;

				candidates.push({
					region: rm, kindID, x: px, y: py,
					trunkR: rk.collision.trunk, crownR: rk.collision.crown ?? 0,
					tiebreak, rank, variantIndex, scale, rotation, tint, depthKey,
				});
			}
		}
	}
	}); // end placer.candidates

	// ── Sort for arbitration: dominance DESC, then tiebreak ASC. Higher-dominance candidates are
	// inserted first (they win contested spots); at equal dominance the LOWER tiebreak-hash wins. ──
	candidates.sort((a, b)=>(b.region.dominance - a.region.dominance) || (a.tiebreak - b.tiebreak));

	// ── Arbitrate + emit. The arbiter sees the FULL candidate set (zoom-/budget-independent
	// canonical layout); rank + the per-tile cap filter EMISSION (a nested subset). Only
	// tile-interior candidates are emitted (D5 margin band inserts but doesn't emit). ──
	const arbiter = new CollisionHash(SCENERY_COLLISION_BUCKET);
	const out = new Map<string, SceneryInstance[]>();
	const placedByRegion = new Map<string, number>();
	let emitted = 0;
	timeIt("placer.arbitrate", ()=>{
	for (const c of candidates) {
		const qRadius = Math.max(c.trunkR + globalMaxTrunk, c.crownR > 0 ? c.crownR + globalMaxCrown : 0);
		// PERF: queryAny short-circuits + allocates nothing (vs query's fresh array per candidate).
		if (arbiter.queryAny(c.x, c.y, qRadius, e=>overlaps(e, c))) continue;
		arbiter.insert({x: c.x, y: c.y, trunkR: c.trunkR, crownR: c.crownR, regionID: c.region.region.id});

		// Emission (display/budget filter — D6 rank AFTER arbitration, so placement stays zoom- and
		// budget-independent; thinning is a nested subset).
		if (c.x < minX || c.x >= minX + size || c.y < minY || c.y >= minY + size) continue;
		if (c.rank >= threshold) continue;
		if (emitted >= maxInstancesPerTile) break; // per-tile cap — degrade, never throw
		let list = out.get(c.kindID);
		if (list == null) { list = []; out.set(c.kindID, list); }
		list.push({
			x: c.x, y: c.y, scale: c.scale, rotation: c.rotation, tint: c.tint,
			variantIndex: c.variantIndex, depthKey: c.depthKey, rank: c.rank,
		});
		if (opts.collectRegionCounts) {
			placedByRegion.set(c.region.region.id, (placedByRegion.get(c.region.region.id) ?? 0) + 1);
		}
		emitted++;
	}
	}); // end placer.arbitrate

	return {instancesByKind: out, placedByRegion};
}

/** Weighted pick of an index from a [0,1) hash, over a list of (unnormalised) weights. Used for
 *  BOTH the variant list (`variants[i].weight`) and — Set16 §3 — the region kind draw
 *  (`SceneryKindEntry.weight`). Uniform weights (zero/absent) → equal probability. */
function pickWeighted(h: number, items: readonly {weight: number}[]): number {
	if (items.length == 0) return 0;
	const total = items.reduce((a, v)=>a + Math.max(0, v.weight), 0);
	if (total <= 0) return Math.min(items.length - 1, Math.floor(h * items.length));
	let acc = 0;
	const target = h * total;
	for (let i = 0; i < items.length; i++) {
		acc += Math.max(0, items[i].weight);
		if (target < acc) return i;
	}
	return items.length - 1;
}

/** Smoothstep ramp (spec-clean: edge0 < edge1). */
function smoothstep(edge0: number, edge1: number, x: number): number {
	if (edge1 <= edge0) return x <= edge0 ? 1 : 0; // degenerate: hard at the threshold
	const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)));
	return t * t * (3 - 2 * t);
}
